import {
  createContext,
  type ReactNode,
  use,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react';
import type { LoginInput } from '@tasks/contracts/auth';
import { login as callLogin } from '#lib/auth/auth-api';
import { authSession, BOOT_TIMEOUT_MS, type SessionState } from '#lib/auth/auth-session';
import { vittoSession } from '#lib/auth/vitto-session';

export type AuthValue = {
  state: SessionState;
  /** true en cuanto la sesión ha estado viva alguna vez en esta carga de la página. */
  wasAuthenticated: boolean;
  signIn: (input: LoginInput) => Promise<void>;
  signOut: () => void;
  retry: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

/** Los tests de componentes inyectan un valor sin arrancar la sesión real. */
export const AuthContextForTests = AuthContext;

export const useAuth = (): AuthValue => {
  const value = use(AuthContext);
  if (!value) throw new Error('useAuth outside AuthProvider');
  return value;
};

const subscribe = (listener: () => void): (() => void) => authSession.subscribe(listener);

export type AuthProviderProps = {
  children: ReactNode;
  /** Solo los tests lo bajan: con el valor real cada prueba del arranque tardaría 5 s. */
  bootTimeoutMs?: number;
};

export const AuthProvider = ({ children, bootTimeoutMs = BOOT_TIMEOUT_MS }: AuthProviderProps) => {
  const state = useSyncExternalStore(
    subscribe,
    () => authSession.current,
    () => 'unknown' as SessionState,
  );
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    // Timeout: con la API caída el arranque no puede quedarse en blanco indefinidamente.
    const timeout = setTimeout(() => setBooted(true), bootTimeoutMs);
    void authSession.boot().finally(() => {
      clearTimeout(timeout);
      setBooted(true);
    });
    return () => clearTimeout(timeout);
  }, [bootTimeoutMs]);

  const value: AuthValue = {
    state: booted ? state : 'unknown',
    wasAuthenticated: authSession.wasAuthenticated,
    signIn: async (input) => {
      authSession.accept(await callLogin(input));
      // a password login is not a Vitto session: nothing of an earlier one may dress it (U2b-2)
      vittoSession.clear();
    },
    signOut: () => authSession.signOut(),
    retry: async () => {
      await authSession.renew();
    },
  };
  return <AuthContext value={value}>{children}</AuthContext>;
};
